import * as React from 'react';
import { connect } from 'react-redux';
import { actions } from 'react-redux-form';
import { find, merge, sortBy, debounce } from 'lodash';
import * as moment from 'moment';
import * as validator from 'validator';
import { Form, Notification } from '../';
import { getBookingCollectionTimes } from '../../redux/actions/bookings';
import { getHireCost } from '../../redux/actions/vehicleTypes';
import FIELDS from '../../data/fields';

interface Props {
  booking: any;
  handleUpdate: Function;
  handleError?: Function;
  vehicleTypes: Array<any>;
  companies: Array<any>;
  dispatch: Function;
  updateOnChange: boolean;
  isDisabled: boolean;
  fields?: any;
}

interface State {
  times: Array<any>;
  vehicleTypeId: number;
  startDate: any;
  endDate: any;
  hire_cost_per_day_actual: number;
  isLoadingTimes: boolean;
  isLoadingHireCost: boolean;
  error: string | boolean;
  formDispatch?: any;
}

class BookingUpdateHireForm extends React.Component<Props, State> {
  constructor(props) {
    super(props);
    this.state = {
      times: [],
      vehicleTypeId: this.props.booking.vehicle_type_id,
      startDate: this.props.booking.start,
      endDate: this.props.booking.end,
      hire_cost_per_day_actual: this.props.booking.hire_cost_per_day_actual,
      isLoadingTimes: true,
      isLoadingHireCost: false,
      error: false,
    };
    // The hire cost is calculated based off start and end fields which can be
    // entered by keyboard so debounced to lower request rate
    this.updateHireCost = debounce(this.updateHireCost.bind(this), 250);
  }

  /**
   * Get the hire code for the vehicle type and update the form
   * @param {Date} startDate
   * @param {Date} endDate
   */
  async updateHireCost(vehicleTypeId: number, startDate: Date, endDate: Date) {
    const id = vehicleTypeId || this.state.vehicleTypeId;
    const start = startDate || this.state.startDate;
    const end = endDate || this.state.endDate;
    if (
      id !== this.state.vehicleTypeId ||
      (startDate &&
        !moment(startDate).isSame(moment(this.state.startDate), 'day')) ||
      (endDate && !moment(endDate).isSame(moment(this.state.endDate), 'day'))
    ) {
      this.setState({ error: false });
      const response = await this.props.dispatch(getHireCost(id, start, end));
      if (response.error) {
        this.setState({ error: response.error });
        return;
      }
      this.state.formDispatch(
        actions.change(
          'generic.hire_cost_per_day',
          response.data.hire_cost_per_day,
        ),
      );
      this.setState({
        endDate,
        vehicleTypeId: id,
        hire_cost_per_day_actual: response.data.hire_cost_per_day,
      });
    }
    this.setState({ isLoadingHireCost: false });
  }

  /**
   * Handle field validation
   * @param {Object} data
   * @return {Object|Boolean}
   */
  handleValidate(data): any {
    const throwError = (error) => {
      this.setState({ error });
      return false;
    };

    if (validator.isEmpty(data.end_date)) {
      return throwError('Please specify an end date');
    }
    const end = moment(`${data.end_date} 23:59:59`);
    if (!end.isValid()) {
      return throwError('The end date is not in a valid format');
    }

    // Add additional validation if a PENDING booking
    if (this.props.booking.status === 'PENDING') {
      const start = moment(`${data.start_date} ${data.start_time}:00`);
      if (!end.isSameOrAfter(start)) {
        return throwError(
          'Make sure the booking end date is after the start date',
        );
      }
      if (validator.isEmpty(data.start_date)) {
        return throwError('Please specify a start date');
      }
      if (!find(this.state.times, { value: data.start_time })) {
        return throwError('Please specify a collection time');
      }
      if (!start.isValid()) {
        throwError('The start date is not in a valid format');
      }
      if (!validator.isNumeric(String(data.vehicle_type_id))) {
        return throwError('Please specify a vehicle type');
      }
      let company_id = data.company_id;
      if (!validator.isNumeric(String(data.company_id))) {
        company_id = '';
      }
      // Clear error message
      throwError(false);
      return {
        company_id,
        start: start.format('YYYY-MM-DD HH:mm:ss'),
        end: end.format('YYYY-MM-DD HH:mm:ss'),
        vehicle_type_id: Number(data.vehicle_type_id),
        hire_cost_per_day: Number(data.hire_cost_per_day),
      };
    }

    // Validate end date with the stored start date if not PENDING
    if (!end.isSameOrAfter(moment(this.props.booking.start))) {
      return throwError(
        'Make sure the booking end date is after the start date',
      );
    }

    const validatedData: any = {
      end: end.format('YYYY-MM-DD HH:mm:ss'),
      hire_cost_per_day: Number(data.hire_cost_per_day),
      mileage_after: Number(data.mileage_after) || 0,
      extra_mileage_rate: Number(data.extra_mileage_rate),
    };

    if (data.return_date) {
      validatedData.return_date = `${moment(data.return_date).format(
        'YYYY-MM-DD',
      )} ${moment(this.props.booking.return_date).format('HH:mm:ss')}`;
    }

    return validatedData;
  }

  render() {
    const handleChange = (data) => {
      if (
        (!this.state.isLoadingTimes ||
          this.props.booking.status !== 'PENDING') &&
        !this.state.isLoadingHireCost
      ) {
        const validatedData = this.handleValidate(data);
        if (this.props.handleError !== undefined) {
          this.props.handleError(validatedData === false);
        }
        if (validatedData !== false) {
          this.props.handleUpdate(
            merge({}, validatedData, {
              hire_cost_per_day_actual: this.state.hire_cost_per_day_actual,
            }),
          );
        }
      }
    };

    return (
      <div className="field">
        <Form
          fields={
            this.props.fields
              ? this.props.fields
              : this.props.booking.status === 'ON-HOLD'
                ? FIELDS.bookings.finalForm
                : this.props.booking.status === 'PENDING'
                  ? FIELDS.bookings.updatePendingHire
                  : FIELDS.bookings.updateHire
          }
          values={{
            start_time: this.state.times,
            vehicle_type_id: this.props.vehicleTypes,
            company_id: this.props.companies,
          }}
          type="generic"
          buttonText="Apply"
          handleSubmit={handleChange}
          hasButton={!this.props.updateOnChange}
          // Handle dynamic collection times
          handleChange={async (data) => {
            if (this.props.booking.status === 'PENDING') {
              const startDate = data.start_date;
              if (startDate && startDate !== this.state.startDate) {
                this.setState({ isLoadingTimes: true }, async () => {
                  const times = await this.props.dispatch(
                    getBookingCollectionTimes(startDate),
                  );
                  if (!times.error) {
                    this.setState({
                      startDate,
                      times: times.data.map((time) => ({
                        name: time,
                        value: time,
                      })),
                      isLoadingTimes: false,
                    });
                  }
                });
              }
            }
            if (this.props.updateOnChange) {
              handleChange(data);
            }
            this.setState({ isLoadingHireCost: true });
            this.updateHireCost(
              Number(data.vehicle_type_id),
              data.start_date,
              data.end_date,
            );
          }}
          disabled={this.props.isDisabled}
          // Handle default value population
          getDispatch={(dispatch) => {
            const booking = this.props.booking;
            let { start, end, return_date } = booking;
            start = moment(start).format('YYYY-MM-DD HH:mm:ss');
            end = moment(end).format('YYYY-MM-DD HH:mm:ss');
            return_date = moment(return_date).format('YYYY-MM-DD HH:mm:ss');
            if (this.props.booking.status === 'PENDING') {
              dispatch(
                actions.change(
                  'generic.vehicle_type_id',
                  booking.vehicle_type_id || '',
                ),
              );
              dispatch(
                actions.change(
                  'generic.start_date',
                  start.substring(0, 10) || '',
                ),
              );
              dispatch(
                actions.change(
                  'generic.start_time',
                  start.substring(11, 16) || '',
                ),
              );
              dispatch(
                actions.change('generic.company_id', booking.company_id || ''),
              );
            }
            if (booking.return_date) {
              dispatch(
                actions.change(
                  'generic.return_date',
                  return_date.substring(0, 10) || '',
                ),
              );
            }
            dispatch(
              actions.change('generic.end_date', end.substring(0, 10) || ''),
            );
            dispatch(
              actions.change(
                'generic.hire_cost_per_day',
                booking.hire_cost_per_day || '0',
              ),
            );
            ['extra_mileage_rate', 'mileage_after'].forEach((field) => {
              dispatch(actions.change(`generic.${field}`, booking[field]));
            });
            this.setState({ formDispatch: dispatch });
          }}
        />
        {this.state.error !== false && <div className="field" />}
        {this.state.error !== false && (
          <div className="field">
            <Notification type="danger">{this.state.error}</Notification>
          </div>
        )}
      </div>
    );
  }
}

const mapStateToProps = ({ vehicleTypes, companies }) => ({
  vehicleTypes: vehicleTypes.vehicleTypes.map((vehicleType) => ({
    name: vehicleType.name,
    value: vehicleType.id,
  })),
  companies: sortBy(companies.companies, 'name').map((company) => ({
    name: company.name,
    value: company.id,
  })),
});

export default connect(mapStateToProps)(BookingUpdateHireForm);
